/**
 * A pot that exists, at its own address — and the link you can put on it.
 *
 * `/prepare/:id` is the pot, `/prepare/:id/share` the composer. Both render the
 * same components the add sheet used to host as modes; what a route adds is a
 * place to come back to. The sheet's tile still opens the pot by name, the flow
 * lands here after storing, and a pot bookmarked last week still answers.
 *
 * ## Eating from here
 *
 * The sheet knows which meal and which day; this page knows neither, so it
 * asks for the meal first, defaulting to the one the clock suggests. The write
 * goes through the same queue as the tile's — the day and the fridge count
 * move on screen before the server has heard of it — but without the sheet's
 * undo tray. The row on Today is the receipt, and removing it puts the portion
 * back, which the page says in so many words.
 */

import {
  helpingFor,
  helpingBody,
  localDate,
  useBatch,
  type Helping,
  type Meal,
} from "@calorio/core";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useOffline } from "../app/offline";
import { useCalorio } from "../app/providers";
import { MealPicker, mealForHour } from "../components/MealPicker";
import { PotDetail, ShareComposer } from "../components/PotDetail";
import { portionHint } from "../components/pot";

export function PreparePot() {
  const { id = "" } = useParams();
  const ctx = useCalorio();
  const offline = useOffline();
  const navigate = useNavigate();
  const batch = useBatch(ctx, id);
  const [meal, setMeal] = useState<Meal>(() => mealForHour(new Date().getHours()));

  const eat = (helping: Helping) => {
    const pot = batch.data;
    if (!pot) return;
    const helped = helpingFor(pot, helping);
    if (!helped) return;
    // `portions` is what the fridge is charged on screen; the body says grams
    // when the plate was weighed and the server derives the same count.
    offline.logPortion(
      {
        batchId: pot.id,
        meal,
        portions: helped.portions,
        ...helpingBody(helping),
      },
      portionHint(pot, helped, localDate(ctx.timeZone)),
    );
    navigate("/");
  };

  return (
    <div className="prepare-pot">
      <div className="editor__back">
        <button className="editor__back-link" onClick={() => navigate("/prepare")}>
          ‹ Prepare
        </button>
      </div>
      <h1 className="editor__title">{batch.data?.recipe_name ?? "This pot"}</h1>

      {/* Asked before the action rather than after it: the button below says
          where the portion is going, and it can only say so once it knows. */}
      <MealPicker value={meal} onChange={setMeal} />

      <PotDetail
        batchId={id}
        meal={meal}
        onEat={eat}
        onShare={() => navigate(`/prepare/${id}/share`)}
        onClose={() => navigate("/prepare")}
      />

      <p className="muted prepare-pot__note">
        Eating from here writes to Today. Undo it by removing the row there — the portion goes back
        in the pot.
      </p>
    </div>
  );
}

export function PrepareShare() {
  const { id = "" } = useParams();
  const ctx = useCalorio();
  const navigate = useNavigate();
  const batch = useBatch(ctx, id);

  return (
    <div className="prepare-pot">
      <div className="editor__back">
        <button className="editor__back-link" onClick={() => navigate(`/prepare/${id}`)}>
          ‹ {batch.data?.recipe_name ?? "This pot"}
        </button>
      </div>
      <h1 className="editor__title">Share this pot</h1>

      <ShareComposer batchId={id} />
    </div>
  );
}
